import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { uploadFile } from '../../utils/fileStorage';

import EmployerHeaderNav from '../../components/navigation/EmployerHeaderNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import EmailChangeModal from '../../components/settings/EmailChangeModal';

// Import modular components
import SecurityTab from './components/SecurityTab';
import EnhancedCompanyProfileForm from '../../components/forms/EnhancedCompanyProfileForm';
import NotificationSoundToggle from '../../components/settings/NotificationSoundToggle';
import SubscriptionManagement from '../../components/subscription/SubscriptionManagement';
import EnhancedNotificationsTab from './components/EnhancedNotificationsTab';
import EmployerPrivacyTab from './components/EmployerPrivacyTab';
import { SUPPORTED_CURRENCIES } from '../../utils/currencyUtils';

const EmployerProfileSettings = () => {
  const { user, userProfile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get tab from URL hash, default to 'profile'
  const getTabFromUrl = () => {
    const hash = location.hash.replace('#', '');
    const validTabs = ['profile', 'company', 'subscription', 'team', 'notifications', 'privacy', 'security'];
    return validTabs.includes(hash) ? hash : 'profile';
  };
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState(getTabFromUrl);
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [emailChangeNotification, setEmailChangeNotification] = useState('');
  const fileInputRef = useRef(null);
  
  // Use ref to track the last processed profile ID to prevent infinite loops
  const lastProfileIdRef = useRef(null);

  // Form states
  const [profileData, setProfileData] = useState({
    full_name: '',
    company_name: '',
    email: '',
    phone: '',
    location: '',
    bio: '',
    website: '',
    linkedin: '',
    company_size: '',
    industry: '',
    founded_year: '',
    preferred_currency: 'USD',
    notification_settings: {
      email: {
        newApplications: true,
        applicationUpdates: true,
        messages: true,
        candidateMatches: true,
        paymentUpdates: true,
        projectUpdates: true,
        weeklyDigest: true,
        marketingEmails: false,
        securityAlerts: true,
        systemUpdates: false,
        contractReminders: true,
        interviewReminders: true
      },
      push: {
        messages: true,
        newApplications: true,
        urgentUpdates: true
      },
      frequency: {
        digestFrequency: 'daily'
      }
    },
    privacy_settings: {
      company_profile_visible: true,
      contact_info_visible: false,
      job_posts_visible: true,
      company_size_visible: true,
      industry_visible: true,
      benefits_visible: true,
      culture_visible: true,
      allow_direct_contact: true,
      show_hiring_activity: true,
      allow_profile_indexing: true,
      public_company_page: false,
      hide_from_search: false,
      anonymous_browsing: false,
      show_employee_reviews: true,
      allow_salary_disclosure: false
    }
  });

  const [companySettings, setCompanySettings] = useState({
    company_logo: '',
    company_description: '',
    company_vision: '',
    company_mission: '',
    company_values: [],
    hiring_process: '',
    company_culture: '',
    remote_work_policy: 'hybrid',
    benefits: []
  });

  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    application_notifications: true,
    interview_reminders: true,
    message_notifications: true,
    weekly_reports: true,
    marketing_emails: false
  });

  const [teamMembers, setTeamMembers] = useState([]);

  // Sync activeTab with URL hash changes
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    const validTabs = ['profile', 'company', 'subscription', 'team', 'notifications', 'privacy', 'security'];
    if (validTabs.includes(hash) && hash !== activeTab) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  // Load company settings from user profile - prevent infinite loops
  useEffect(() => {
    if (userProfile && userProfile.id && lastProfileIdRef.current !== userProfile.id) {
      console.log('Loading employer profile data for user:', userProfile.id);
      setProfileData({
        full_name: userProfile.full_name || '',
        company_name: userProfile.company_name || '',
        email: userProfile.email || '',
        phone: userProfile.phone || '',
        location: userProfile.location || '',
        bio: userProfile.bio || '',
        website: userProfile.website || '',
        linkedin: userProfile.linkedin || '',
        company_size: userProfile.company_size || '',
        industry: userProfile.industry || '',
        founded_year: userProfile.founded_year || '',
        preferred_currency: userProfile.preferred_currency || 'USD',
        notification_settings: userProfile.notification_settings || {
          email: {
            newApplications: true,
            applicationUpdates: true,
            messages: true,
            candidateMatches: true,
            paymentUpdates: true,
            projectUpdates: true,
            weeklyDigest: true,
            marketingEmails: false,
            securityAlerts: true,
            systemUpdates: false,
            contractReminders: true,
            interviewReminders: true
          },
          push: {
            messages: true,
            newApplications: true,
            urgentUpdates: true
          },
          frequency: {
            digestFrequency: 'daily'
          }
        },
        privacy_settings: userProfile.privacy_settings || {
          company_profile_visible: true,
          contact_info_visible: false,
          job_posts_visible: true,
          company_size_visible: true,
          industry_visible: true,
          benefits_visible: true,
          culture_visible: true,
          allow_direct_contact: true,
          show_hiring_activity: true,
          allow_profile_indexing: true,
          public_company_page: false,
          hide_from_search: false,
          anonymous_browsing: false,
          show_employee_reviews: true,
          allow_salary_disclosure: false
        }
      });

      // Load company settings if available
      setCompanySettings({
        company_logo: userProfile.company_logo || '',
        company_description: userProfile.company_description || '',
        company_vision: userProfile.company_vision || '',
        company_mission: userProfile.company_mission || '',
        company_values: userProfile.company_values || [],
        hiring_process: userProfile.hiring_process || '',
        company_culture: userProfile.company_culture || '',
        remote_work_policy: userProfile.remote_work_policy || 'hybrid',
        benefits: userProfile.benefits || []
      });
      
      // Load notification settings if available
      if (userProfile.notification_settings) {
        setNotificationSettings(userProfile.notification_settings);
      }
      
      // Load team members if available
      if (userProfile.team_members) {
        setTeamMembers(userProfile.team_members);
      }
      
      // Load security settings if available
      if (userProfile.security_settings) {
        setSecuritySettings(userProfile.security_settings);
      }
      
      lastProfileIdRef.current = userProfile.id;
    }
  }, [userProfile]);

  const validateForm = (data, section) => {
    const newErrors = {};

    if (section === 'profile') {
      if (!data.full_name?.trim()) newErrors.full_name = 'Full name is required';
      if (!data.company_name?.trim()) newErrors.company_name = 'Company name is required';
      if (!data.email?.trim()) newErrors.email = 'Email is required';
      if (data.email && !/^\S+@\S+\.\S+$/.test(data.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (data.phone && !/^\+?[\d\s-()]+$/.test(data.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }
      if (data.website && !/^https?:\/\/.+/.test(data.website)) {
        newErrors.website = 'Please enter a valid website URL (include http:// or https://)';
      }
    }

    return newErrors;
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    const validationErrors = validateForm(profileData, 'profile');
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    try {
      const result = await updateProfile(profileData);
      if (result.success) {
        setSuccessMessage('Profile updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      setErrors({ general: 'Failed to update profile. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Simple file validation
    if (file.size > 2 * 1024 * 1024) {
      setErrors({ company_logo: 'Logo file size should be less than 2MB' });
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setErrors({ company_logo: 'Only JPG and PNG files are allowed' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Upload file to Supabase storage
      const result = await uploadFile(
        file, 
        'company-logos', 
        `employer-${user.id}`,
        'logo-'
      );

      if (result.success) {
        // Update company settings with new logo URL
        setCompanySettings({
          ...companySettings,
          company_logo: result.data.url
        });

        setSuccessMessage('Logo uploaded successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrors({ company_logo: result.error });
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      setErrors({ company_logo: 'Failed to upload logo. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySettingsUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const result = await updateProfile({ 
        ...profileData,
        ...companySettings
      });
      if (result.success) {
        setSuccessMessage('Company settings updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      setErrors({ general: 'Failed to update company settings. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Handle notification settings save
  const handleNotificationsSave = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const result = await updateProfile({
        notification_settings: profileData.notification_settings
      });
      if (result.success) {
        setSuccessMessage('Notification settings updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      setErrors({ general: 'Failed to update notification settings. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Handle privacy settings save
  const handlePrivacySave = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const result = await updateProfile({
        privacy_settings: profileData.privacy_settings
      });
      if (result.success) {
        setSuccessMessage('Privacy settings updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      setErrors({ general: 'Failed to update privacy settings. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile Information', icon: 'User' },
    { id: 'company', label: 'Enhanced Company Profile', icon: 'Building' },
    { id: 'subscription', label: 'Subscription & Billing', icon: 'CreditCard' },
    { id: 'team', label: 'Team Management (Coming Soon)', icon: 'Users', disabled: true },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'privacy', label: 'Privacy', icon: 'Shield' },
    { id: 'security', label: 'Security', icon: 'Lock' }
  ];

  // Handler to change tab and update URL
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`#${tabId}`, { replace: true });
  };

  // Handle email change initiated
  const handleEmailChangeInitiated = (changeData) => {
    console.log('handleEmailChangeInitiated called with:', changeData);
    
    // Ensure changeData is an object and has the expected properties
    if (!changeData || typeof changeData !== 'object') {
      console.error('Invalid changeData:', changeData);
      setEmailChangeNotification('Email change verification sent. Please check your email to complete the change.');
      return;
    }
    
    // Extract the message or create a default one
    const message = changeData.message || 
      `Email change verification sent to ${changeData.newEmail || 'your new email'}. Please check your email to complete the change.`;
    
    setEmailChangeNotification(message);
    
    // Clear the notification after 10 seconds
    setTimeout(() => {
      setEmailChangeNotification('');
    }, 10000);
  };

  const renderProfileTab = () => (
    <form onSubmit={handleProfileUpdate} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Full Name *
          </label>
          <Input
            type="text"
            value={profileData.full_name}
            onChange={(e) => setProfileData({...profileData, full_name: e.target.value})}
            error={errors.full_name}
            placeholder="Enter your full name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Company Name *
          </label>
          <Input
            type="text"
            value={profileData.company_name}
            onChange={(e) => setProfileData({...profileData, company_name: e.target.value})}
            error={errors.company_name}
            placeholder="Enter company name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Email Address *
          </label>
          <div className="flex space-x-2">
            <Input
              type="email"
              value={profileData.email}
              onChange={(e) => setProfileData({...profileData, email: e.target.value})}
              error={errors.email}
              placeholder="Enter email address"
              disabled // Make email field read-only since changes need verification
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowEmailChangeModal(true)}
              className="px-3 py-2 whitespace-nowrap"
            >
              <Icon name="Edit" size={16} className="mr-1" />
              Change
            </Button>
          </div>
          <p className="text-xs text-text-tertiary mt-1">
            To change your email address, click the "Change" button and verify the new email
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Phone Number
          </label>
          <Input
            type="tel"
            value={profileData.phone}
            onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
            error={errors.phone}
            placeholder="Enter phone number"
          />
        </div>

      </div>

      {errors.general && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <div className="flex">
            <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mr-2" />
            <p className="text-sm text-error">{errors.general}</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-success-50 border border-success-200 rounded-lg p-4">
          <div className="flex">
            <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mr-2" />
            <p className="text-sm text-success">{successMessage}</p>
          </div>
        </div>
      )}

      {emailChangeNotification && (
        <div className="bg-info-50 border border-info-200 rounded-lg p-4">
          <div className="flex">
            <Icon name="Mail" size={20} className="text-info flex-shrink-0 mr-2" />
            <p className="text-sm text-info">{typeof emailChangeNotification === 'string' ? emailChangeNotification : emailChangeNotification.message}</p>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );

  const renderCompanyTab = () => (
    <div>
      <EnhancedCompanyProfileForm />
    </div>
  );

  const renderNotificationsTab = () => (
    <form onSubmit={handleNotificationsSave} className="space-y-6">
      <EnhancedNotificationsTab
        profileData={profileData}
        onInputChange={(field, value) => setProfileData(prev => ({ ...prev, [field]: value }))}
      />

      {/* Error and Success Messages */}
      {errors.general && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <div className="flex">
            <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mr-2" />
            <p className="text-sm text-error">{errors.general}</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-success-50 border border-success-200 rounded-lg p-4">
          <div className="flex">
            <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mr-2" />
            <p className="text-sm text-success">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );

  const handleAddTeamMember = () => {
    setTeamMembers([
      ...teamMembers,
      {
        id: Date.now().toString(),
        name: '',
        email: '',
        role: 'team_member',
        status: 'pending'
      }
    ]);
  };

  const handleTeamMemberChange = (id, field, value) => {
    setTeamMembers(
      teamMembers.map(member => 
        member.id === id ? { ...member, [field]: value } : member
      )
    );
  };

  const handleRemoveTeamMember = (id) => {
    setTeamMembers(teamMembers.filter(member => member.id !== id));
  };

  const handleTeamUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      // Filter out empty team members
      const validTeamMembers = teamMembers.filter(member => 
        member.name.trim() !== '' && member.email.trim() !== ''
      );

      const result = await updateProfile({
        team_members: validTeamMembers
      });

      if (result.success) {
        setSuccessMessage('Team members updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      setErrors({ general: 'Failed to update team members. Please try again.' });
    } finally {
      setLoading(false);
    }
  };
  
  // Security settings state
  const [securitySettings, setSecuritySettings] = useState({
    two_factor_auth: false,
    login_notifications: false,
    show_login_history: false,
    password_expiry_days: 90,
    allowed_ip_addresses: [],
    require_team_2fa: false,
    security_questions_enabled: false
  });
  
  // Function to handle security settings update
  const handleSecurityUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const result = await updateProfile({
        security_settings: securitySettings
      });

      if (result.success) {
        setSuccessMessage('Security settings updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      setErrors({ general: 'Failed to update security settings. Please try again.' });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle password change
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validate password
    if (passwordData.new_password !== passwordData.confirm_password) {
      setErrors({ confirm_password: 'Passwords do not match' });
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      setErrors({ new_password: 'Password must be at least 8 characters long' });
      return;
    }
    
    setLoading(true);
    setErrors({});
    
    try {
      // Call Supabase auth API to update password
      // This is a placeholder - you would need to implement actual password change logic
      const { error } = await supabase.auth.updateUser({
        password: passwordData.new_password
      });
      
      if (error) {
        throw error;
      }
      
      setSuccessMessage('Password updated successfully!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrors({ 
        general: error.message || 'Failed to update password. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  const renderSecurityTab = () => (
    <SecurityTab 
      userProfile={userProfile}
      updateProfile={updateProfile}
    />
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return renderProfileTab();
      case 'company':
        return renderCompanyTab();
      case 'subscription':
        return <SubscriptionManagement />;
      case 'team':
        // Tab disabled for future development
        return (
          <div className="py-12 text-center">
            <Icon name="Clock" size={48} className="mx-auto mb-4 text-text-tertiary opacity-50" />
            <h3 className="text-xl font-semibold text-text-primary mb-2">Team Management Coming Soon</h3>
            <p className="text-text-secondary max-w-md mx-auto">
              This feature is currently under development and will be available in a future update. 
              You'll be able to manage your team members, assign roles, and control permissions.
            </p>
          </div>
        );
      case 'notifications':
        return renderNotificationsTab();
      case 'privacy':
        return (
          <form onSubmit={handlePrivacySave} className="space-y-6">
            <EmployerPrivacyTab
              profileData={profileData}
              onInputChange={(field, value) => setProfileData(prev => ({ ...prev, [field]: value }))}
            />

            {/* Error and Success Messages */}
            {errors.general && (
              <div className="bg-error-50 border border-error-200 rounded-lg p-4">
                <div className="flex">
                  <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mr-2" />
                  <p className="text-sm text-error">{errors.general}</p>
                </div>
              </div>
            )}

            {successMessage && (
              <div className="bg-success-50 border border-success-200 rounded-lg p-4">
                <div className="flex">
                  <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mr-2" />
                  <p className="text-sm text-success">{successMessage}</p>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                loading={loading}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        );
      case 'security':
        return renderSecurityTab();
      default:
        return null;
    }
  };
  
  const renderTeamTab = () => (
    <form onSubmit={handleTeamUpdate} className="space-y-6">
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-text-primary">Team Members</h3>
          <Button 
            type="button"
            variant="outline" 
            size="sm" 
            onClick={handleAddTeamMember}
          >
            <Icon name="Plus" size={16} className="mr-2" />
            Add Team Member
          </Button>
        </div>
        
        {teamMembers.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Users" size={32} className="text-text-muted mx-auto mb-2" />
            <p className="text-text-secondary">No team members added yet</p>
            <p className="text-xs text-text-tertiary mt-1">Add team members to manage your account together</p>
          </div>
        ) : (
          <div className="space-y-4">
            {teamMembers.map((member, index) => (
              <div key={member.id} className="border border-border rounded-lg p-4">
                <div className="flex justify-between mb-2">
                  <h4 className="font-medium">Team Member {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => handleRemoveTeamMember(member.id)}
                    className="text-text-tertiary hover:text-error transition-colors"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Name
                    </label>
                    <Input
                      type="text"
                      value={member.name}
                      onChange={(e) => handleTeamMemberChange(member.id, 'name', e.target.value)}
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Email
                    </label>
                    <Input
                      type="email"
                      value={member.email}
                      onChange={(e) => handleTeamMemberChange(member.id, 'email', e.target.value)}
                      placeholder="Email address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Role
                    </label>
                    <select
                      value={member.role}
                      onChange={(e) => handleTeamMemberChange(member.id, 'role', e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    >
                      <option value="team_member">Team Member</option>
                      <option value="admin">Admin</option>
                      <option value="recruiter">Recruiter</option>
                      <option value="hiring_manager">Hiring Manager</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Status
                    </label>
                    <div className="flex items-center space-x-1 h-[42px] px-3">
                      <span className={`inline-block w-2 h-2 rounded-full ${
                        member.status === 'active' ? 'bg-success' : 
                        member.status === 'pending' ? 'bg-warning' : 'bg-text-tertiary'
                      }`}></span>
                      <span className="text-sm text-text-secondary capitalize">{member.status}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {errors.general && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <div className="flex">
            <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mr-2" />
            <p className="text-sm text-error">{errors.general}</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-success-50 border border-success-200 rounded-lg p-4">
          <div className="flex">
            <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mr-2" />
            <p className="text-sm text-success">{successMessage}</p>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Team Changes'}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen bg-background">
      <EmployerHeaderNav />
      
      <main className="pt-16 pb-20 md:pb-8">
        <div className="content-container px-4 sm:px-6 md:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-4">
              <button
                onClick={() => navigate('/employer-dashboard')}
                className="self-start p-2 rounded-lg border border-border hover:border-primary hover:bg-primary-50 transition-colors duration-200"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">Profile Settings</h1>
                <p className="text-text-secondary">Manage your profile and company information</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
            {/* Sidebar Navigation */}
            <div className="xl:col-span-3">
              <div className="bg-surface rounded-lg border border-border p-4 sticky top-20">
                <div className="space-y-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => !tab.disabled && handleTabChange(tab.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors duration-200 ${
                        activeTab === tab.id
                          ? 'bg-primary-50 text-primary border border-primary-200' : 
                        tab.disabled
                          ? 'text-text-tertiary cursor-not-allowed' :
                        'text-text-secondary hover:text-text-primary hover:bg-secondary-50'
                      }`}
                    >
                      <Icon name={tab.icon} size={18} />
                      <div className="flex flex-col">
                        <span className="font-medium">{tab.label}</span>
                        {tab.disabled && <span className="text-xs text-text-tertiary">Future Development</span>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="xl:col-span-9">
              <div className="bg-surface rounded-lg border border-border p-4 sm:p-6">
                {renderTabContent()}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Email Change Modal */}
      <EmailChangeModal
        isOpen={showEmailChangeModal}
        onClose={() => setShowEmailChangeModal(false)}
        currentEmail={profileData.email}
        userId={user?.id}
        userRole="employer"
        onEmailChangeInitiated={handleEmailChangeInitiated}
      />
    </div>
  );
};

export default EmployerProfileSettings;

